import React, { useCallback, useEffect, useRef } from 'react';

/** A bar a moment, so a word is a few bars. */
export const METER_STEP_MS = 70;
const BAR_WIDTH = 3;
const BAR_GAP = 2;
const MIN_BAR = 2;

/** The bars' colour is the canvas's own CSS colour, so it follows the theme; it is read again this often. */
const COLOUR_EVERY_MS = 500;
/** Bars kept beyond what fits: enough for the quiet that ends a recording (3s), so the voice before it is still there. */
const KEPT_BEYOND = 64;
/** Quieter than this is the room, not the voice. */
const QUIET = 0.12;

interface VoiceMeterProps {
   /** The loudest moment since the last ask, 0–1; null where the voice can't be measured (the bars then lie flat). */
   readLevel: () => number | null;
   /** Listening: a new bar each moment. Otherwise the bars hold still. */
   live: boolean;
}

/**
 * The voice as it is heard: a bar a moment for its loudness, the newest at the end, older ones fainter towards the
 * start. It moves only with the voice; a quiet moment is a short bar, never none. Once listening ends it holds still on
 * the voice, leaving off the quiet the recording stopped on.
 */
export const VoiceMeter: React.FC<VoiceMeterProps> = ({ readLevel, live }) => {
   const canvasRef = useRef<HTMLCanvasElement | null>(null);
   const levels = useRef<number[]>([]);
   const colour = useRef({ value: '', readAt: Number.NEGATIVE_INFINITY });

   const draw = useCallback(() => {
      const canvas = canvasRef.current;
      const context = canvas?.getContext('2d');
      if (!canvas || !context) return;
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;
      const ratio = window.devicePixelRatio || 1;
      if (canvas.width !== Math.round(width * ratio) || canvas.height !== Math.round(height * ratio)) {
         canvas.width = Math.round(width * ratio);
         canvas.height = Math.round(height * ratio);
      }
      const now = Date.now();
      if (now - colour.current.readAt >= COLOUR_EVERY_MS) colour.current = { value: getComputedStyle(canvas).color, readAt: now };

      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      context.clearRect(0, 0, width, height);
      const capacity = Math.max(1, Math.floor((width + BAR_GAP) / (BAR_WIDTH + BAR_GAP)));
      const shown = levels.current.slice(-capacity);
      shown.forEach((level, index) => {
         const age = shown.length - 1 - index;
         const x = width - BAR_WIDTH - age * (BAR_WIDTH + BAR_GAP);
         const barHeight = Math.max(MIN_BAR, level * height * 0.9);
         context.globalAlpha = 1 - 0.65 * (age / capacity);
         context.fillStyle = colour.current.value;
         context.beginPath();
         if (typeof context.roundRect === 'function') context.roundRect(x, (height - barHeight) / 2, BAR_WIDTH, barHeight, BAR_WIDTH / 2);
         else context.rect(x, (height - barHeight) / 2, BAR_WIDTH, barHeight);
         context.fill();
      });
      context.globalAlpha = 1;
      if (levels.current.length > capacity + KEPT_BEYOND) levels.current = levels.current.slice(-(capacity + KEPT_BEYOND));
   }, []);

   useEffect(() => {
      if (!live) {
         // The recording ended on quiet: end on the voice instead (all quiet stays as it was)
         const lastVoice = levels.current.reduce((last, level, index) => (level >= QUIET ? index : last), -1);
         if (lastVoice >= 0 && lastVoice < levels.current.length - 1) {
            levels.current = levels.current.slice(0, lastVoice + 1);
            draw();
         }
         return;
      }
      const timer = window.setInterval(() => {
         levels.current.push(readLevel() ?? 0);
         draw();
      }, METER_STEP_MS);
      return () => window.clearInterval(timer);
   }, [live, readLevel, draw]);

   return <canvas ref={canvasRef} className="cx-voice-meter__bars" aria-hidden="true" />;
};
